import { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FormRow, FormSection } from "@/components/blocks/FormRows"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useFormat } from "@/lib/format"
import type { PageProps } from "@/types/inertia"
import { ProfileSubNav } from "./ProfileSubNav"

type Membership = {
  id: number
  program: string
  expires_on: string | null
  expired: boolean
  active_program: boolean
}

export default function ProfilePrograms({ memberships }: { memberships: Membership[] }) {
  const intl = useIntl()
  const { date } = useFormat()
  const { props } = usePage<PageProps>()
  const form = useForm({ code: "" })
  const error = props.errors?.code
  const description = intl.formatMessage({ id: "profile.programs_meta" })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/redemptions", { preserveScroll: true, onSuccess: () => form.reset() })
  }

  return (
    <>
      <Head title={intl.formatMessage({ id: "profile.programs_title" })}>
        <meta name="description" content={description} />
        <meta property="og:title" content={intl.formatMessage({ id: "profile.programs_title" })} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <PageHeader title={intl.formatMessage({ id: "profile.title" })} description={intl.formatMessage({ id: "profile.subtitle" })} tabs={<ProfileSubNav active="programs" />} />

        <form onSubmit={submit} className="mt-10 max-w-3xl">
          <FormSection
            title={intl.formatMessage({ id: "profile.unlock_title" })}
            description={intl.formatMessage({ id: "profile.unlock_description" })}
          >
            <FormRow label={intl.formatMessage({ id: "profile.code_label" })} htmlFor="access-code">
              <Input
                id="access-code"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required
                aria-invalid={!!error}
                aria-describedby={error ? "access-code-error" : undefined}
                value={form.data.code}
                onChange={(e) => form.setData("code", e.target.value)}
                className="sm:max-w-md"
              />
              {error && (
                <p id="access-code-error" role="alert" className="mt-2 text-xs text-danger-display">
                  {error}
                </p>
              )}
            </FormRow>
          </FormSection>
          <div className="mt-6 flex justify-end">
            <Button type="submit" disabled={form.processing || form.data.code.trim() === ""}>
              {intl.formatMessage({ id: "profile.unlock" })}
            </Button>
          </div>
        </form>

        <div className="mt-12 max-w-3xl">
          <FormSection title={intl.formatMessage({ id: "profile.yours_title" })} description={intl.formatMessage({ id: "profile.yours_description" })}>
            <div className="sm:py-6">
              {memberships.length === 0 ? (
                <p>{intl.formatMessage({ id: "profile.none_yet" })}</p>
              ) : (
                <ul className="list-none space-y-3 pl-0">
                  {memberships.map((m) => (
                    <li key={m.id} className="flex flex-wrap items-center gap-2">
                      <strong>{m.program}</strong>
                      {m.expired ? (
                        <Badge tone="signal">{intl.formatMessage({ id: "profile.ended" }, { date: date(m.expires_on!, { year: "numeric", month: "short", day: "numeric" }) })}</Badge>
                      ) : m.expires_on ? (
                        <Badge tone="neutral">{intl.formatMessage({ id: "profile.until" }, { date: date(m.expires_on, { year: "numeric", month: "short", day: "numeric" }) })}</Badge>
                      ) : (
                        <Badge tone="accent">{intl.formatMessage({ id: "profile.full_access" })}</Badge>
                      )}
                      {!m.active_program && <Badge tone="muted">{intl.formatMessage({ id: "profile.unavailable" })}</Badge>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </FormSection>
        </div>
      </AppShell>
    </>
  )
}
